import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BookOpen, Clock, CheckCircle } from '@phosphor-icons/react';
import Breadcrumbs from '../components/Breadcrumbs';
import type { LearningPath } from '@researcher/shared';
import AppNav from '../components/AppNav';
import AppFooter from '../components/AppFooter';
import PageMeta from '../components/PageMeta';
import LoginModal from '../components/LoginModal';
import { useAuth } from '../contexts/AuthContext';
import { getTopicViews } from '../utils/library';
import { apiUrl } from '../config/api';

export default function LearningPathDetail() {
  const { pathId } = useParams<{ pathId: string }>();
  const { currentUser } = useAuth();
  const [path, setPath] = useState<LearningPath | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completedSlugs, setCompletedSlugs] = useState<Set<string>>(new Set());
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  useEffect(() => {
    if (!pathId) return;
    setLoading(true);

    fetch(apiUrl(`/library/learning-paths/${pathId}`))
      .then(res => res.json())
      .then(data => {
        if (data.success) setPath(data.data);
        else setError('Learning path not found');
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [pathId]);

  // Track progress via topic views
  useEffect(() => {
    if (!currentUser || !path) return;
    getTopicViews()
      .then(slugs => setCompletedSlugs(new Set(slugs)))
      .catch(console.error);
  }, [currentUser, path]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
        <AppNav />
        <div className="max-w-3xl mx-auto px-4 pt-24 pb-16 animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  if (error || !path) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
        <AppNav />
        <div className="max-w-3xl mx-auto px-4 pt-24 pb-16 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error || 'Not found'}</p>
          <Link to="/research" className="text-pink-500 hover:text-pink-600 font-medium text-sm">
            Back to Library
          </Link>
        </div>
      </div>
    );
  }

  const completedCount = path.steps.filter(s => completedSlugs.has(s.topicSlug)).length;
  const progressPct = path.steps.length > 0 ? Math.round((completedCount / path.steps.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
      <PageMeta
        title={`${path.title} — Learning Path`}
        description={path.description}
        path={`/research/learning/${path.slug}`}
      />
      <AppNav onLoginOpen={() => setIsLoginOpen(true)} />

      <main className="pt-16 pb-16 md:pb-0">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          {/* Breadcrumb */}
          <Breadcrumbs current={path.title} />

          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
              <BookOpen size={22} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">{path.title}</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {path.steps.length} topics &middot; {path.totalReadTimeMinutes} min &middot; {path.difficulty}
              </p>
            </div>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{path.description}</p>

          {/* Progress bar */}
          {currentUser && completedCount > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                <span>{completedCount} of {path.steps.length} completed</span>
                <span>{progressPct}%</span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-pink-500 to-orange-500 rounded-full transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}

          {/* Completion banner */}
          {currentUser && progressPct === 100 && (
            <div className="mb-6 relative overflow-hidden rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 p-5 text-white text-center">
              <div className="confetti-container" aria-hidden="true">
                {[...Array(20)].map((_, i) => (
                  <span
                    key={i}
                    className="confetti-dot"
                    style={{
                      left: `${Math.random() * 100}%`,
                      animationDelay: `${Math.random() * 2}s`,
                      backgroundColor: ['#f472b6', '#facc15', '#60a5fa', '#a78bfa', '#34d399'][i % 5],
                    }}
                  />
                ))}
              </div>
              <CheckCircle size={32} weight="fill" className="mx-auto mb-2" />
              <p className="font-bold text-lg">You completed {path.title}!</p>
              <p className="text-sm text-green-100 mt-1">Great job working through all {path.steps.length} topics.</p>
              <style>{`
                .confetti-container { position: absolute; inset: 0; pointer-events: none; }
                .confetti-dot {
                  position: absolute;
                  width: 6px; height: 6px;
                  border-radius: 50%;
                  top: -8px;
                  animation: confetti-fall 3s ease-in forwards;
                }
                @keyframes confetti-fall {
                  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                  100% { transform: translateY(200px) rotate(720deg); opacity: 0; }
                }
              `}</style>
            </div>
          )}

          {/* Steps */}
          <div className="space-y-3">
            {path.steps.map((step, i) => {
              const isCompleted = completedSlugs.has(step.topicSlug);
              return (
                <Link
                  key={step.topicSlug}
                  to={`/research/topics/${step.topicSlug}?from=${path.slug}`}
                  className="flex items-start gap-3 bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {isCompleted ? (
                      <CheckCircle size={22} weight="fill" className="text-green-500" />
                    ) : (
                      <div className="w-[22px] h-[22px] rounded-full border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center text-[10px] font-bold text-gray-400">
                        {i + 1}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{step.title}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{step.description}</p>
                  </div>
                  <span className="text-[11px] text-gray-400 flex items-center gap-1 flex-shrink-0">
                    <Clock size={12} /> {step.readTimeMinutes}m
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </main>

      <AppFooter />
      {isLoginOpen && <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />}
    </div>
  );
}
